import { AppIcon, type IconDefinition } from '@/components/ui/app-icon';
import { cn } from '@/lib/utils';
import type { HTMLAttributes, ReactNode, Ref } from 'react';

/**
 * Gumloop's card in the agent chat (their "gummie element"), measured: a shell
 * with a 1px ring and a soft layered shadow, a 36px header strip (a 20px icon
 * tile, the title at 12/16 500, anything trailing at the right), and a white
 * body with its own ring and a lighter shadow. The question card, the play
 * proposal, the confirmation and the connection card all sit in it.
 *
 * Pixel values on purpose: they are Gumloop's measured sizes.
 */
export const AGENT_CARD_SHELL_SHADOW =
  'shadow-[0_0_0_1px_hsl(var(--border)),0_1px_1px_-0.5px_rgba(0,0,0,0.024),0_3px_3px_-1.5px_rgba(0,0,0,0.024),0_6px_6px_-3px_rgba(0,0,0,0.02),0_12px_12px_-6px_rgba(0,0,0,0.02),0_24px_24px_-12px_rgba(0,0,0,0.02)]';

export const AGENT_CARD_BODY_SHADOW =
  'shadow-[0_0_0_1px_hsl(var(--border)),0_1px_1px_-0.5px_rgba(0,0,0,0.016),0_3px_3px_-1.5px_rgba(0,0,0,0.016),0_6px_6px_-3px_rgba(0,0,0,0.01),0_12px_12px_-6px_rgba(0,0,0,0.01),0_24px_24px_-12px_rgba(0,0,0,0.01)]';

/** The card's 32px buttons: 12/16 500, 12px sides, radius 8. */
export const AGENT_CARD_BUTTON =
  'inline-flex h-[32px] shrink-0 items-center justify-center gap-[8px] whitespace-nowrap rounded-[8px] px-[12px] text-[12px] font-medium leading-[16px] transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50';

/** Bordered, on the page background (Back, Reject, Cancel). */
export const AGENT_CARD_BUTTON_OUTLINE = cn(
  AGENT_CARD_BUTTON,
  'border border-border bg-background text-foreground hover:bg-muted/60'
);

/** Gumloop's dark button, the card's primary action (Next, Submit, Confirm,
 * Review on canvas, Connect). */
export const AGENT_CARD_BUTTON_DARK = cn(
  AGENT_CARD_BUTTON,
  'border border-foreground/90 bg-[linear-gradient(to_bottom,hsl(var(--foreground)/0.82),hsl(var(--foreground)/0.94))] text-background shadow-[inset_0_1.5px_0_0_rgba(255,255,255,0.24)] [--cl-base:currentColor] hover:bg-[linear-gradient(to_bottom,hsl(var(--foreground)/0.9),hsl(var(--foreground)))]'
);

/** The confirm button of a delete or a merge, in the destructive color. */
export const AGENT_CARD_BUTTON_DESTRUCTIVE = cn(
  AGENT_CARD_BUTTON,
  'border border-destructive bg-destructive text-destructive-foreground hover:bg-destructive/90 dark:border-red-700 dark:bg-red-700 dark:hover:bg-red-700/90'
);

/** Every text link on a card (Details, Open list, Review all): 12/16 500,
 *  underlined. A card's extra actions are links, so it keeps two buttons. */
export const CARD_LINK =
  'shrink-0 cursor-pointer bg-transparent text-[12px] font-medium leading-[16px] whitespace-nowrap text-foreground/80 underline decoration-border underline-offset-[3px] hover:text-foreground disabled:pointer-events-none disabled:opacity-50';

export function AgentCard({
  ref,
  icon,
  leading,
  title,
  titleLines = 1,
  trailing,
  children,
  className,
  bodyClassName,
  ...rest
}: {
  ref?: Ref<HTMLDivElement>;
  /** The header tile's icon (10px, muted). */
  icon?: IconDefinition;
  /** Replaces the icon tile, e.g. with an app's logo. */
  leading?: ReactNode;
  title: ReactNode;
  /** 2 lets a long title wrap onto a second line, the header growing with
   *  it, instead of cutting it beside what trails it. */
  titleLines?: 1 | 2;
  trailing?: ReactNode;
  children?: ReactNode;
  bodyClassName?: string;
} & Omit<HTMLAttributes<HTMLDivElement>, 'title'>) {
  return (
    <div
      ref={ref}
      {...rest}
      className={cn(
        'relative flex w-full min-w-0 flex-col overflow-clip rounded-[8px] bg-muted/50 text-foreground',
        AGENT_CARD_SHELL_SHADOW,
        className
      )}
    >
      <div
        className={cn(
          'flex items-center gap-[8px] px-[10px] py-[8px]',
          titleLines === 2 ? 'min-h-[36px]' : 'h-[36px]'
        )}
      >
        {leading ?? (
          <div className='flex size-[20px] flex-none items-center justify-center rounded-[6px] border border-border bg-background text-muted-foreground'>
            {icon ? <AppIcon icon={icon} style={{ fontSize: 10 }} /> : null}
          </div>
        )}
        <p
          className={cn(
            'min-w-0 text-[12px] leading-[16px] font-medium text-foreground',
            titleLines === 2 ? 'line-clamp-2' : 'line-clamp-1'
          )}
        >
          {title}
        </p>
        {trailing ? (
          <div className='ml-auto flex shrink-0 items-center gap-[4px]'>
            {trailing}
          </div>
        ) : null}
      </div>
      {children ? (
        <div
          className={cn(
            'flex flex-col gap-[16px] rounded-[8px] bg-card p-[12px]',
            AGENT_CARD_BODY_SHADOW,
            bodyClassName
          )}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}
